import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

export default function Staff() {
  const { user } = useAuth();
  const readOnly = user?.role === "staff";
  const [staff, setStaff] = useState([]);
  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Supprimer ce compte ?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Personnel</h1>
          <p className="text-sm text-slate-500">
            {readOnly ? "Consultation de l'équipe (lecture seule)" : "Créateurs, Admins et Staff"}
          </p>
        </div>
        {!readOnly && <NewStaffDialog role={user?.role} onCreated={load} />}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
              <tr><th className="text-left p-3">Nom</th><th className="text-left">Email</th><th className="text-left">Rôle</th><th className="text-left">Statut</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.email}`}>
                  <td className="p-3 flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-500/20 text-orange-500 grid place-items-center"><User className="w-4 h-4" /></div>{s.full_name}</td>
                  <td>{s.email}</td>
                  <td><Badge variant="outline" className="capitalize">{s.role}</Badge></td>
                  <td>{s.active ? <span className="text-emerald-600">Actif</span> : <span className="text-slate-500">Désactivé</span>}</td>
                  <td className="p-3 text-right">
                    {!readOnly && s.id !== user?.id && (
                      <Button variant="ghost" size="icon" onClick={() => del(s.id)} data-testid={`delete-staff-${s.email}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Aucun personnel</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewStaffDialog({ role, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "staff", permissions: [] });

  const roles = role === "createur" || role === "super_admin"
    ? [{ v: "staff", l: "Staff" }, { v: "admin", l: "Admin" }, { v: "createur", l: "Créateur (max 2)" }]
    : [{ v: "staff", l: "Staff" }];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-pill bg-orange-500 hover:bg-orange-600" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau compte</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Ajouter un membre</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom complet</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="new-staff-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-staff-email" /></div>
          <div><Label>Mot de passe (min 6)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-staff-password" /></div>
          <div><Label>Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="new-staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="btn-pill bg-orange-500 hover:bg-orange-600 w-full" data-testid="save-staff-btn" onClick={async () => {
            try { await api.post("/staff", form); toast.success("Membre ajouté"); setOpen(false); setForm({ email: "", full_name: "", password: "", role: "staff", permissions: [] }); onCreated(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
